import { useCallback, useMemo, useState } from "react"

import { uploadAudioFileWithSignedPost } from "@/audio"
import {
  transport,
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"

import type { BackgroundSoundResource } from "@/types_api/backend/transport/list_background_sounds_response"
import type { UpdateBackgroundSoundRequest } from "@/types_api/backend/transport/update_background_sound_transport_request"

type BackgroundSoundAcousticSourceType = "ambient" | "point_source"

interface UseBackgroundSoundsOptions {
  includeArchived?: boolean
  enabled?: boolean
}

interface UploadBackgroundSoundInput {
  file: File
  displayName?: string
  defaultVolume?: number
  acousticSourceType?: BackgroundSoundAcousticSourceType
}

type UpdateBackgroundSoundInput = Omit<
  UpdateBackgroundSoundRequest,
  "background_sound_id"
> & {
  id: string
}

const DEFAULT_BACKGROUND_SOUND_NAME = "Background sound"
const DEFAULT_AUDIO_CONTENT_TYPE = "audio/mpeg"

/**
 * Fallback default-volume multiplier used when an uploaded background sound
 * does not specify its own default. Matches the platform-side built-in mid-range.
 */
const DEFAULT_BACKGROUND_SOUND_VOLUME = 0.3

function normalizeUploadDisplayName(file: File, displayName?: string) {
  const trimmedName = displayName?.trim()
  if (trimmedName) {
    return trimmedName
  }
  return file.name.replace(/\.[^.]+$/, "") || DEFAULT_BACKGROUND_SOUND_NAME
}

export function useBackgroundSoundsTransport(
  options?: UseBackgroundSoundsOptions,
) {
  const includeArchived = options?.includeArchived ?? true
  const transportQueryUtils = useTransportQueryUtils()
  // Tracks the raw S3 PUT phase, which is not covered by the create/complete mutation pending flags.
  const [isUploadingToStorage, setIsUploadingToStorage] = useState(false)

  const request = useMemo(
    () => ({ include_archived: includeArchived }),
    [includeArchived],
  )

  const query = useTransportQuery<
    "listBackgroundSounds",
    BackgroundSoundResource[]
  >({
    operation: transport.operations.listBackgroundSounds,
    request,
    options: {
      enabled: options?.enabled ?? true,
      select: (response) => response.background_sounds,
    },
  })

  const invalidateBackgroundSounds = useCallback(async () => {
    await Promise.all([
      transportQueryUtils.invalidateCacheTags(["background-sounds"]),
      transportQueryUtils.invalidateCacheTags(["personas"]),
    ])
  }, [transportQueryUtils])

  const createMutation = useTransportMutation(
    transport.operations.createBackgroundSound,
  )
  const completeMutation = useTransportMutation(
    transport.operations.completeBackgroundSound,
  )
  const updateMutation = useTransportMutation(
    transport.operations.updateBackgroundSound,
    {
      onSuccess: () => {
        void invalidateBackgroundSounds()
      },
    },
  )

  const uploadBackgroundSound = useCallback(
    async ({
      file,
      displayName,
      defaultVolume = DEFAULT_BACKGROUND_SOUND_VOLUME,
      acousticSourceType,
    }: UploadBackgroundSoundInput) => {
      const pending = await createMutation.mutateAsync({
        display_name: normalizeUploadDisplayName(file, displayName),
        original_filename: file.name,
        content_type: file.type || DEFAULT_AUDIO_CONTENT_TYPE,
        default_volume: defaultVolume,
        ...(acousticSourceType
          ? { acoustic_source_type: acousticSourceType }
          : {}),
      })

      // Enforce the server-provided size cap before transferring the whole file to S3.
      if (pending.max_size_bytes && file.size > pending.max_size_bytes) {
        const maxMegabytes = Math.floor(pending.max_size_bytes / (1024 * 1024))
        throw new Error(
          `Audio file is too large. The maximum background sound size is ${maxMegabytes} MB.`,
        )
      }

      setIsUploadingToStorage(true)
      try {
        await uploadAudioFileWithSignedPost({
          url: pending.upload_url,
          fields: pending.upload_fields,
          file,
        })
      } finally {
        setIsUploadingToStorage(false)
      }

      const completed = await completeMutation.mutateAsync({
        background_sound_id: pending.background_sound.id,
      })

      await invalidateBackgroundSounds()
      return completed.background_sound
    },
    [completeMutation, createMutation, invalidateBackgroundSounds],
  )

  const updateBackgroundSound = useCallback(
    async ({ id, ...request }: UpdateBackgroundSoundInput) => {
      const response = await updateMutation.mutateAsync({
        background_sound_id: id,
        ...request,
      })
      return response.background_sound
    },
    [updateMutation],
  )

  return {
    ...query,
    backgroundSounds: query.data ?? [],
    uploadBackgroundSound,
    updateBackgroundSound,
    isUploading:
      createMutation.isPending ||
      isUploadingToStorage ||
      completeMutation.isPending,
    isUpdating: updateMutation.isPending,
  }
}
